<template>
  <n-card>
    <user-email-dialog
      v-model="isNewMakerUserDialogOpened"
      :maker="firstMaker"
      title="Renseigner un email de contact"
      message="Ce prestataire n'a aucune adresse email pour le contacter, vous pouvez en ajouter une ici"
      submit-label="Ajouter et continuer"
      cancel-label="Continuer sans email"
      :can-close="true"
      @cancel="createWithoutMakerEmail"
      @save="makerUserAdded"
    />
    <intervention-date-dialog
      v-model="isInterventionDateDialogOpened"
      :intervention="fakeInterventionForDateDialog"
      :default-status-id="selectedStatusId"
      :default-assignee="selectedAssignee"
      submit-text="Valider"
      no-comment
      catch-submit
      @close="isInterventionDateDialogOpened = false"
      @submit="interventionDateChoosen"
    />
    <template v-if="!isLoaded">
      <v-row no-gutters justify="center" align="center" class="my-5">
        <v-progress-circular indeterminate color="deep-purple accent-3"></v-progress-circular>
      </v-row>
    </template>
    <template v-else>
      <v-card-text>
        <h2 class="pb-6 pt-3">
          <template v-if="isCreatingAsClient">
            Prestataire et lieu d'intervention
          </template>
          <template v-else>
            Client et lieu d'intervention
          </template>
        </h2>
        <div class="pb-6">
          <v-row no-gutters justify="center" align="center">
            <v-col class="pr-2" style="flex-basis: 0;">
              <v-text-field
                label="Client"
                v-model="client.name"
                hide-details
                outlined
                prepend-inner-icon="business"
                readonly
                v-if="isCreatingAsMaker"
              />
              <v-text-field
                label="Prestataire"
                v-model="maker.name"
                hide-details
                outlined
                prepend-inner-icon="construction"
                readonly
                v-else
              />
            </v-col>
            <v-col class="pl-2" style="flex-basis: 0;">
              <v-text-field
                label="Site"
                v-model="site.name"
                hide-details
                outlined
                prepend-inner-icon="place"
                readonly
              />
            </v-col>
          </v-row>

          <h3 class="pb-3 pt-5">Précisez au besoin les détails du lieu d'intervention (optionnel)</h3>
          <v-row no-gutters justify="center" align="center">
            <v-col class="pr-2" cols="2">
              <v-text-field
                label="Étage"
                v-model="floor"
                hide-details
                outlined
              />
            </v-col>
            <v-col class="pl-2 pr-2" cols="3">
              <v-text-field
                label="Pièce"
                v-model="room"
                hide-details
                outlined
              />
            </v-col>
            <v-col class="pl-2" cols="7">
              <v-text-field
                label="Précision du lieu"
                v-model="placeDetails"
                hide-details
                outlined
              />
            </v-col>
          </v-row>
          <div class="pt-8">
            <v-divider />
          </div>
        </div>


        <h2 class="pb-6 pt-3">Détails de l'intervention</h2>
        <template v-if="isLoaded">
          <v-text-field
            :error="titleError"
            label="Titre de l'intervention*"
            v-model="title"
            autofocus
            hide-details
            outlined
            prepend-inner-icon="title"
          />
          <v-textarea
            label="Description"
            placeholder="Si besoin décrivez plus en détails votre demande..."
            v-model="description"
            hide-details
            outlined
            prepend-inner-icon="message"
            height="200"
            class="mt-3"
          />
          <v-row no-gutters align="center" justify="center" class="mt-3" v-for="makerData in makersData" :key="makerData.id">
            <v-col>
              <v-autocomplete
                :error="makerData.ui.isPriorityError"
                label="Priorité*"
                :items="prioritiesByMaker[makerData.id]"
                v-model="priority"
                item-value="id"
                item-text="name"
                return-object
                hide-details
                outlined
                :no-data-text="autocompletePriorityNoDataText"
                append-icon=""
                class="mr-2"
              />
            </v-col>
            <v-col>
              <v-autocomplete
                :error="makerData.ui.isDomainError"
                label="Domaine*"
                :items="domainsByMaker[makerData.id]"
                v-model="domain"
                item-value="id"
                item-text="name"
                return-object
                hide-details
                outlined
                :no-data-text="autocompleteDomainNoDataText"
                append-icon=""
                class="ml-2"
              />
            </v-col>
          </v-row>
          <v-text-field
            :error="displayNumError"
            label="Numéro d'intervention personnalisé"
            placeholder="Numéro d'intervention personnalisé (optionnel)"
            v-model="displayNum"
            hide-details
            clearable
            outlined
            append-icon=""
            class="mt-3 mb-3"
          />
          <template v-if="isUserOnMaker">
            <h3 class="pb-3 pt-3">Plannification</h3>
            <v-row no-gutters class="mt-3">
              <v-col>
                <v-autocomplete
                  :error="autocompleteStatusError"
                  label="Statut*"
                  :items="statusesWOLast"
                  v-model="selectedStatusId"
                  item-value="id"
                  item-text="name"
                  hide-details
                  outlined
                  :no-data-text="autocompleteStatusNoDataText"
                  append-icon=""
                  class="mr-2"
                />
              </v-col>
              <v-col>
                <v-autocomplete
                  label="Assignée à"
                  :items="makerUsersWName"
                  v-model="selectedAssigneeId"
                  item-value="id"
                  item-text="name"
                  hide-details
                  clearable
                  outlined
                  :no-data-text="autocompleteAssigneeNoDataText"
                  append-icon=""
                  class="ml-2"
                />
              </v-col>
              <v-col class="pl-4" v-if="isMaker || isSelfMaker">
                <v-btn
                  outlined
                  color="grey"
                  class="n-btn n-large-btn"
                  style="width: 100%;"
                  @click="isInterventionDateDialogOpened = true"
                >
                  <v-icon left color="accent">event</v-icon>
                  <span class="font-weight-medium grey--text text--darken-2" v-if="!hasInterventionDate">
                    Définir une date d'intervention
                  </span>
                  <span class="font-weight-medium grey--text text--darken-2" v-else>
                    {{ interventionDateDatas | formatInterventionDate }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </template>
          <div class="pt-6" v-if="areEquipmentsActivated">
            <h3 class="pl-1">Équipements concernés<template v-if="isEquipmentsRequired">*</template></h3>
            <div class="mt-2" v-if="equipmentsError">
              <v-alert dense outlined type="error">Merci de préciser au moins un équipement</v-alert>
            </div>
            <n-card class="form-attachment px-3 mt-2" v-for="equipment in equipments" :key="`equipment-${equipment.id}`">
              <div class="pt-1 pb-1">
                <v-row no-gutters align="center">
                  <v-icon color="primary" class="ml-1 mr-2">memory</v-icon>
                  <v-col style="font-size: 15px;">
                    {{ equipment.label }}
                  </v-col>
                  <v-btn fab icon depressed color="red" text small class="mr-0" @click="unlinkEquipment(equipment)">
                    <v-icon>delete_outline</v-icon>
                  </v-btn>
                </v-row>
              </div>
            </n-card>
            <div class="mt-2" v-if="!hasSelectedSite">
              <v-alert dense outlined type="info">Veuillez sélectionner un site pour ajouter des équipements</v-alert>
            </div>
            <div class="mt-2" v-else>
              <v-menu
                v-model="availableEquipmentsMenu"
                min-width="450"
                offset-y
                :close-on-content-click="false"
                bottom
              >
                <template v-slot:activator="{ attrs }">
                  <v-btn
                    text
                    small
                    class="ml-0"
                    v-bind="attrs"
                    @click="openAvailableEquipmentsMenu"
                  >
                    <v-icon class="mr-1">add</v-icon>Ajouter un équipement
                  </v-btn>
                </template>
                <n-card>
                  <div class="px-4 py-6" v-if="isFetchingAvailableEquipments">
                    <v-progress-linear
                      color="primary"
                      indeterminate
                      rounded
                      height="6"
                    ></v-progress-linear>
                  </div>
                  <v-list dense class="py-0 overflow-y-auto" max-height="400px" v-else>
                    <div class="pa-3">
                      <v-text-field
                        v-model="searchAvailableEquipments"
                        outlined
                        solo
                        flat
                        hide-details
                        autofocus
                        prepend-inner-icon="search"
                        placeholder="Chercher un équipement"
                      />
                    </div>
                    <div class="pa-5 font-italic" v-if="!availableEquipments.length">
                      Aucun équipement trouvé
                    </div>
                    <template v-for="equipment in availableEquipments">
                      <v-list-item :key="equipment.id" @click="linkEquipment(equipment)">
                        <v-list-item-content>
                          <v-list-item-title>
                            <template v-if="equipment.number">
                              {{ equipment.number }}
                              <span class="px-1">-</span>
                            </template>
                            {{ equipment.label }}
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="equipment.model || equipment.brand">
                            <template v-if="equipment.brand">
                              {{ equipment.brand }}
                            </template>
                            <template v-if="equipment.model && equipment.brand">
                              <span class="px-1">-</span>
                            </template>
                            <template v-if="equipment.model">
                              {{ equipment.model }}
                            </template>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle>
                            <template v-if="equipment.serialNumber">
                              {{ equipment.serialNumber }}
                            </template>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider :key="`divider-${equipment.id}`" />
                    </template>
                  </v-list>
                </n-card>
              </v-menu>
            </div>
          </div>
          <div class="pt-5">
            <h3 class="pl-1">Pièces jointes</h3>
            <n-card class="form-attachment px-3 mt-2" v-for="(attachment, i) in attachments" :key="`attachment-${attachment.uid}`">
              <div class="pt-1 pb-1">
                <v-row no-gutters align="center">
                  <v-icon color="primary" class="ml-1 mr-2">attach_file</v-icon>
                  <v-col>
                    <template v-if="attachment.file === null">
                      <input :id="`attachment-input-${attachment.uid}`" type="file" @change="loadAttachment(attachment, $event)" />
                      <label :for="`attachment-input-${attachment.uid}`">Choisissez un fichier</label>
                    </template>
                    <v-row no-gutters align="center" style="font-size: 15px;" v-else>
                      {{ attachment.file.name }}
                    </v-row>
                  </v-col>
                  <v-btn fab icon depressed color="red" text small class="mr-0" @click="removeAttachmentByIndex(i)">
                    <v-icon>delete_outline</v-icon>
                  </v-btn>
                </v-row>
              </div>
            </n-card>
            <div class="mt-2">
              <v-btn text small class="ml-0" @click="addNewAttachement()">
                <v-icon class="mr-1">add</v-icon>Ajouter une pièce jointe
              </v-btn>
            </div>
          </div>
          <div class="pt-5" v-if="areMakerTagsActivated">
            <h3 class="pl-1">Tags</h3>
            <v-row no-gutters justify="start" align="center" class="px-2 py-1">
              <tag-chip v-for="tag in makerTags" :key="tag.id" :tag="tag" @remove="removeMakerTag" removable />
              <div>
                <v-menu
                  v-model="isShowingTagsList"
                  min-width="350"
                  :close-on-content-click="false"
                  offset-y
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      color="grey"
                      small
                      outlined
                      class="my-1 mx-1"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left class="mr-1">add</v-icon>
                      Ajouter un tag
                    </v-chip>
                  </template>
                  <tags-menu-list
                    :maker-id="slugClient.id"
                    :excluded-tags="makerTags"
                    @selected="makerTagSelected"
                    v-if="isShowingTagsList"
                  />
                </v-menu>
              </div>
            </v-row>
          </div>
          <div class="pt-5" v-if="areClientTagsActivated">
            <h3 class="pl-1">Tags</h3>
            <v-row no-gutters justify="start" align="center" class="px-2 py-1">
              <tag-chip v-for="tag in clientTags" :key="tag.id" :tag="tag" @remove="removeClientTag" removable />
              <div>
                <v-menu
                  v-model="isShowingTagsList"
                  min-width="350"
                  :close-on-content-click="false"
                  offset-y
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      color="grey"
                      small
                      outlined
                      class="my-1 mx-1"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left class="mr-1">add</v-icon>
                      Ajouter un tag
                    </v-chip>
                  </template>
                  <tags-menu-list
                    :client-id="slugClient.id"
                    :excluded-tags="clientTags"
                    @selected="clientTagSelected"
                    v-if="isShowingTagsList"
                  />
                </v-menu>
              </div>
            </v-row>
          </div>
          <input type="submit" value="" style="display: none;">
        </template>
        <v-row no-gutters class="mt-4">
          <v-spacer></v-spacer>
          <v-btn
            ccolor="primary"
            text
            class="mr-3 pl-3 pr-3"
            @click="cancel()"
            :disabled="isCreating"
          >
            Annuler
          </v-btn>
          <v-btn
            color="primary darken-1"
            class="mr-1 pl-4 pr-4"
            @click="createTicket()"
            :loading="isCreating"
          >
            Créer l'intervention
          </v-btn>
        </v-row>
      </v-card-text>
    </template>
  </n-card>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import {
  mapState, mapGetters, mapMutations, mapActions,
} from 'vuex';
import { uuid } from 'vue-uuid';
import api from '@/api';
import TagsMenuList from '@/components/Tags/MenuList.vue';
import InterventionDateDialog from '@/components/Interventions/InterventionDateDialog.vue';
import UserEmailDialog from '@/components/Users/UserEmailDialog.vue';

export default {
  data: () => ({
    BASE_URL: INDEEGO_BASE_URL, //eslint-disable-line
    dialog: true,
    isLoaded: false,
    makerObj: null,
    clientObj: null,
    makersData: [],
    // equipments: [],
    attachments: [],
    // selectedPriorityId: null,
    // selectedDomainId: null,
    selectedStatusId: null,
    selectedAssigneeId: null,
    displayNum: ``,
    // title: ``,
    // description: ``,
    plannedStartAt: null,
    plannedEndAt: null,
    plannedDaysDuration: null,
    isPlannedHourly: false,
    clientTags: [],
    makerTags: [],
    autocompletePriorityError: false,
    autocompleteDomainError: false,
    autocompleteStatusError: false,
    displayNumError: false,
    titleError: false,
    isCreating: false,
    availableEquipmentsMenu: false,
    isFetchingAvailableEquipments: false,
    searchAvailableEquipments: ``,
    equipmentsError: false,
    isShowingTagsList: false,
    interventionDetails: {},
    isNewMakerUserDialogOpened: false,
    isInterventionDateDialogOpened: false,
  }),
  computed: {
    ...mapState(`model/user`, [
      `me`,
      `isMakerUser`,
      `isAggregatorUser`,
    ]),
    ...mapState(`model/client`, [
      `company`,
      `isMaker`,
      `isSelfMaker`,
      `isAggregator`,
    ]),
    ...mapState(`model/client`, {
      slugClient: `company`,
    }),
    ...mapGetters(`model/intervention`, [`colorFromBG`]),
    ...mapState(`ui/newIntervention`, {
      availableEquipments(state) {
        const addedEquipmentsIds = _.map(this.equipments, `id`);
        return _.chain(state.availableEquipments)
          .filter(equipment => !_.includes(addedEquipmentsIds, equipment.id))
          .map(equipment => ({
            ...equipment,
            searchString: _.toLower(_.deburr(`${equipment.number} - ${equipment.label} - ${equipment.brand} - ${equipment.model} - ${equipment.serialNumber}`)),
          }))
          .filter(equipment => _.includes(equipment.searchString, _.toLower(_.deburr(this.searchAvailableEquipments))))
          .value();
      },
    }),
    ...mapState(`ui/newIntervention`, [
      `prioritiesByMaker`,
      `dbClients`,
      `client`,
      `makers`,
      `site`,
      `inboxIntervention`,
    ]),
    ...mapGetters(`ui/newIntervention`, [
      `isCreatingAsClient`,
      `isCreatingAsMaker`,
      `firstMaker`,
      `hasInboxIntervention`,
    ]),
    ...mapState(`ui/newIntervention`, {
      statuses: `dbStatuses`,
      prioritiesByMaker: `dbPrioritiesByMaker`,
      domainsByMaker: state => {
        const domainsByMaker = {};
        _.forEach(state.dbDomainsByMaker, (value, key) => {
          domainsByMaker[key] = _.sortBy(value || [], domain => _.toLower(domain.name));
        });

        return domainsByMaker;
      },
      storeMakerUsers: `dbMakerUsers`,
      storeFloor: `floor`,
      storeRoom: `room`,
      storePlaceDetails: `placeDetails`,
      storeTitle: `title`,
      storeDescription: `description`,
      storePriority: `priority`,
      storeDomain: `domain`,
      storeEquipments: `equipments`,
    }),
    floor: {
      get() { return this.storeFloor; },
      set(floor) { this.SET_FLOOR(floor); },
    },
    room: {
      get() { return this.storeRoom; },
      set(room) { this.SET_ROOM(room); },
    },
    placeDetails: {
      get() { return this.storePlaceDetails; },
      set(placeDetails) { this.SET_PLACE_DETAILS(placeDetails); },
    },
    title: {
      get() { return this.storeTitle; },
      set(title) { this.SET_TITLE(title); },
    },
    description: {
      get() { return this.storeDescription; },
      set(description) { this.SET_DESCRIPTION(description); },
    },
    priority: {
      get() { return this.storePriority; },
      set(priority) { this.SET_PRIORITY(priority); },
    },
    domain: {
      get() { return this.storeDomain; },
      set(domain) { this.SET_DOMAIN(domain); },
    },
    equipments: {
      get() { return this.storeEquipments; },
      set(equipments) { this.SET_EQUIPMENTS(equipments); },
    },
    maker() {
      return this.makers[0];
    },
    sites() {
      return this.sitesByMaker[this.makers[0].id];
    },
    priorities() {
      return this.prioritiesByMaker[this.makers[0].id];
    },
    domains() {
      return this.domainsByMaker[this.makers[0].id];
    },
    makerUsers() {
      return _.chain(this.storeMakerUsers)
        .filter(user => user.invitationStatus === 2)
        .filter(user => !user.isExcludedFromPlanning)
        .value();
    },
    isChallengingIntervention() {
      return this.makers.length > 1;
    },
    areEquipmentsActivated() {
      return this.makers.length === 1 && this.makers[0].rights.equipments === true;
    },
    areMakerTagsActivated() {
      return this.makers.length === 1 && this.isUserOnMaker && this.isMaker;
    },
    areClientTagsActivated() {
      return this.isAggregator && this.isAggregatorUser;
    },
    isUserOnMaker() {
      if (
        this.me
        && (
          (this.makers.length === 1 && this.makers[0].id !== -1 && parseInt(this.me.Client_id, 10) === parseInt(this.makers[0].id, 10))
          || parseInt(this.me.id, 10) === 1
        )
      ) {
        return true;
      }

      return false;
    },
    filteredAndOrderedSites() {
      let { searchSiteString, sites } = this;
      searchSiteString = searchSiteString || ``;
      if (!this.site || this.site.name !== searchSiteString) {
        sites = _.filter(this.sites, site => {
          const searchString = `${site.name} ${site.address} ${site.postalCode} ${site.city}`.replace(/ {2}/g, ` `);
          return searchString.toLocaleLowerCase().indexOf(searchSiteString.toLocaleLowerCase()) > -1;
        });
      }
      return _.concat([],
        sites,
        { id: -1 }
      );
    },
    canCreateSite() {
      return (this.company.id === this.me.Client_id && this.me.clientAdmin) || this.me.isIndeego;
    },
    statusesWOLast() {
      return _.filter(this.statuses, { isLast: false });
    },
    makerUsersWName() {
      return this.makerUsers.map(user => ({
        ...user,
        name: `${user.firstname} ${user.lastname}`,
      }));
    },
    hasSelectedSite() {
      return this.site !== null && this.site.id;
    },
    selectedSiteName() {
      let label = ``;
      if (this.hasSelectedSite) {
        label = this.site.name;
      }
      return label;
    },
    selectedSiteComment() {
      if (!this.hasSelectedSite || !this.site.comment || !this.site.comment.length) { return ``; }

      return this.site.comment.replace(/[\n|\r|\n\r]/g, `<br>`);
    },
    isEquipmentsRequired() {
      return this.client.options.equipmentsRequiredInIntervention === true && !this.isUserOnMaker;
    },
    autocompleteSiteNoDataText() {
      return !this.isLoaded ? `Récupération des sites en cours...` : `Aucun site existant`;
    },
    autocompletePriorityNoDataText() {
      return !this.isLoaded ? `Récupération des priorités en cours...` : `Aucune priorité existant`;
    },
    autocompleteDomainNoDataText() {
      return !this.isLoaded ? `Récupération des domaines en cours...` : `Aucun domaine existant`;
    },
    autocompleteStatusNoDataText() {
      return !this.isLoaded ? `Récupération des statuts en cours...` : `Aucun statut existant`;
    },
    autocompleteAssigneeNoDataText() {
      return !this.isLoaded ? `Récupération des utilisateurs en cours...` : `Aucun utilisateur displnible`;
    },
    selectedAssignee() {
      if (!this.selectedAssigneeId) {
        return null;
      }

      return _.find(this.makerUsers, { id: this.selectedAssigneeId });
    },
    hasInterventionDate() {
      return Boolean(this.plannedStartAt && this.plannedEndAt);
    },
    interventionDateDatas() {
      if (!this.hasInterventionDate) { return {}; }

      return {
        plannedStartAt: this.plannedStartAt,
        plannedEndAt: this.plannedEndAt,
        isPlannedHourly: this.isPlannedHourly,
        plannedDaysDuration: this.plannedDaysDuration,
      };
    },
    fakeInterventionForDateDialog() {
      return {
        id: -1,
        Maker_id: this.firstMaker.id,
        ...this.interventionDateDatas,
      };
    },
  },
  methods: {
    ...mapMutations(`ui/newIntervention`, [
      `SET_FLOOR`,
      `SET_ROOM`,
      `SET_PLACE_DETAILS`,
      `SET_TITLE`,
      `SET_DESCRIPTION`,
      `SET_PRIORITY`,
      `SET_DOMAIN`,
      `SET_EQUIPMENTS`,
    ]),
    ...mapActions(`ui/newIntervention`, [
      `markInboxInterventionAccepted`,
    ]),
    cancel() {
      this.$emit(`cancel`);
    },
    async fetchAvailableEquipments() {
      this.isFetchingAvailableEquipments = true;
      await this.$store.dispatch(`ui/newIntervention/fetchAvailableEquipments`);
      this.isFetchingAvailableEquipments = false;
    },
    async openAvailableEquipmentsMenu() {
      this.searchAvailableEquipments = ``;
      this.availableEquipmentsMenu = true;
    },
    linkEquipment(equipment) {
      this.equipmentsError = false;
      this.equipments.push(equipment);
      this.availableEquipmentsMenu = false;
    },
    unlinkEquipment(equipment) {
      this.equipments = _.without(this.equipments, equipment);
    },
    addNewAttachement() {
      this.attachments.push({
        uid: uuid.v4(),
        file: null,
        fileBase64: null,
      });
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(
          reader.result.replace(/^[^,]+,/g, ``)
        );
        reader.onerror = error => reject(error);
      });
    },
    async loadAttachment(attachment, event) {
      [attachment.file] = event.target.files;
      attachment.fileBase64 = await this.fileToBase64(attachment.file);
    },
    removeAttachmentByIndex(index) {
      this.attachments.splice(index, 1);
    },
    addClientTags(tags) {
      this.clientTags = _.chain(this.clientTags)
        .concat(tags)
        .uniqBy(`id`)
        .value();
    },
    clientTagSelected(tag) {
      this.isShowingTagsList = false;
      this.clientTags.push(tag);
    },
    removeClientTag(tag) {
      const interventionIndex = _.findIndex(this.clientTags, { id: tag.id });
      if (interventionIndex > -1) {
        this.clientTags.splice(interventionIndex, 1);
      }
    },
    addMakerTags(tags) {
      this.makerTags = _.chain(this.makerTags)
        .concat(tags)
        .uniqBy(`id`)
        .value();
    },
    makerTagSelected(tag) {
      this.isShowingTagsList = false;
      this.makerTags.push(tag);
    },
    removeMakerTag(tag) {
      const interventionIndex = _.findIndex(this.makerTags, { id: tag.id });
      if (interventionIndex > -1) {
        this.makerTags.splice(interventionIndex, 1);
      }
    },
    createWithoutMakerEmail() {
      this.processCreate();
    },
    makerUserAdded() {
      this.processCreate();
    },
    interventionDateChoosen(infos) {
      this.isInterventionDateDialogOpened = false;
      const plannedDaysDuration = moment(infos.plannedEndAt, `YYYYMMDDHHmm`)
        .diff(
          moment(infos.plannedStartAt, `YYYYMMDDHHmm`), `days`
        ) + 1;
      this.plannedStartAt = infos.plannedStartAt;
      this.plannedEndAt = infos.plannedEndAt;
      this.plannedDaysDuration = plannedDaysDuration;
      this.isPlannedHourly = infos.isPlannedHourly;
      this.selectedStatusId = infos.statusId;
      this.selectedAssigneeId = infos.assigneeId;
    },
    clearInterventionDate() {
      this.plannedStartAt = null;
      this.plannedEndAt = null;
      this.plannedDaysDuration = null;
      this.isPlannedHourly = null;
    },
    async processCreate() {
      this.isCreating = true;

      const { data: intervention } = await api.post(`/interventions`, this.interventionDetails);

      if (this.hasInboxIntervention) {
        this.markInboxInterventionAccepted();
      }

      if (this.isChallengingIntervention && intervention) {
        this.$router.push({
          name: `InterventionsChallengingIntervention`,
          params: {
            challengingInterventionId: intervention.id,
          },
        });
      } else if (intervention && intervention.num) {
        this.$router.push({
          name: `InterventionsIntervention`,
          params: {
            interventionId: intervention.id,
          },
        });
      }

      this.isCreating = false;
    },
    async createTicket() {
      if (!this.isUserOnMaker) {
        this.selectedStatusId = -1;
      }

      this.autocompleteStatusError = false;
      this.titleError = false;
      this.equipmentsError = false;

      let hasMakersDataError = false;

      const makerData = this.makersData[0];
      makerData.ui.isPriorityError = !this.priority?.id;
      makerData.ui.isDomainError = !this.domain?.id;
      hasMakersDataError = makerData.ui.isPriorityError || makerData.ui.isDomainError;

      if (!this.selectedStatusId) { this.autocompleteStatusError = true; }
      if (!_.trim(this.title).length) { this.titleError = true; }
      if (this.areEquipmentsActivated && this.isEquipmentsRequired && !this.equipments.length) { this.equipmentsError = true; }

      if (this.selectedSiteError
        || hasMakersDataError
        || this.autocompleteStatusError
        || this.titleError
        || this.equipmentsError
      ) {
        return;
      }

      makerData.Priority_id = this.priority.id;
      makerData.Domain_id = this.domain.id;

      if (this.isCreating) { return; }
      this.isCreating = true;

      const title = _.trim(this.title);
      const description = _.trim(this.description);

      const site = parseInt(this.site.id, 10);
      const status = parseInt(this.selectedStatusId, 10);
      const assignee = parseInt(this.selectedAssigneeId, 10) || null;
      const client = parseInt(this.client.id, 10) || null;

      this.interventionDetails = {
        Site_id: site,
        Status_id: status,
        Assignee_id: assignee,
        Client_id: client,
        InboxIntervention_id: this.inboxIntervention?.id || null,
        RecurrentIntervention_id: this.inboxIntervention?.RecurrentIntervention_id || null, // eslint-disable-line
        makersData: this.makersData,
        displayNum: this.displayNum,
        floor: this.floor || null,
        room: this.room || null,
        placeDetails: this.placeDetails || null,
        title,
        description,
        equipments: this.equipments,
        clientTags: this.clientTags,
        makerTags: this.makerTags,
        attachments: this.attachments.map(attachment => ({
          filename: attachment.file.name,
          mimeType: attachment.file.type,
          content: attachment.fileBase64,
        })),
        ...this.interventionDateDatas,
      };

      let hasEmail = true;

      if (this.isAggregator && this.isAggregatorUser) {
        hasEmail = await this.$store.dispatch(`ui/newIntervention/checkIfMakerHasEmail`);
      }

      if (!hasEmail) {
        this.isCreating = false;
        this.isNewMakerUserDialogOpened = true;
      } else {
        this.processCreate();
      }
    },
  },

  watch: {
    selectedPriorityId() { this.autocompletePriorityError = false; },
    selectedDomainId() { this.autocompleteDomainError = false; },
    description() { this.titleError = false; },
    statuses() {
      const status = _.find(this.statuses, { sort: 1 });
      if (status) {
        this.selectedStatusId = status.id;
      }
    },
    availableEquipmentsMenu() {
      if (this.availableEquipmentsMenu) {
        this.fetchAvailableEquipments();
      }
    },
  },
  async beforeMount() {
    this.isLoaded = false;
    this.clientTags = [];
    this.makerTags = [];
    const promises = [];
    promises.push(
      this.$store.dispatch(`ui/newIntervention/fetchPriorities`),
      this.$store.dispatch(`ui/newIntervention/fetchDomains`)
    );

    if (this.isUserOnMaker) {
      promises.push(this.$store.dispatch(`ui/newIntervention/fetchStatuses`));
      promises.push(this.$store.dispatch(`ui/newIntervention/fetchMakerUsers`));
    }

    if (!this.isMaker) {
      promises.push(this.$store.dispatch(`ui/newIntervention/fetchClients`));
    }

    await Promise.all(promises);

    if (!this.isMaker) {
      this.$store.commit(`ui/newIntervention/SET_CLIENT`, this.dbClients[0]);
    }

    this.addMakerTags(this.client.makerTags || []);

    this.addClientTags(this.site.clientTags || []);
    this.addMakerTags(this.site.makerTags || []);

    this.makers.forEach(maker => {
      const makerDomains = this.domainsByMaker[maker.id];
      const makerPriorities = this.prioritiesByMaker[maker.id];
      this.addClientTags(maker.clientTags || []);
      this.priority = makerPriorities.length === 1 ? makerPriorities[0] : null;
      this.domain = makerDomains.length === 1 ? makerDomains[0] : null;
      this.makersData.push({
        ..._.pick(maker, [`id`, `name`]),
        Site_id: null,
        Priority_id: makerPriorities.length === 1 ? makerPriorities[0].id : null,
        Domain_id: makerDomains.length === 1 ? makerDomains[0].id : null,
        Status_id: null,
        ui: {
          isPriorityError: false,
          isDomainError: false,
        },
      });
    });

    this.isLoaded = true;
    this.$emit(`ready`);
  },
  components: {
    TagsMenuList,
    InterventionDateDialog,
    UserEmailDialog,
  },
};
</script>

<style lang="scss">
  .site-btn {
    border: 1px solid rgba(0,0,0,0.38);
    border-color: rgba(0,0,0,0.38);
    border-radius: 4px;
    font-size: 16px;
    padding: 16px 12px;
    cursor: pointer;

    &.placeholder {
      color: #333333;
    }

    &.custom-error {
      border: 2px solid #ff5252 !important;
      color: #ff5252 !important;
      background-color: inherit !important;
    }

    &:hover {
      border-color: rgba(0,0,0,0.87);
      border-radius: 4px;
      font-size: 16px;
    }
  }

  .select-site-modal {
    position: absolute;
    top: 0;
    width: 600px;
    max-width: 95%;
  }

  .site-comment {
    margin-bottom: 20px;
    margin-left: 20px;
    border-left: 3px solid #4610dc;
    border-radius: 4px;
    background-color: #3300c01a;
    padding: 12px;
    line-height: 14px;
  }

  .v-alert.maker-label .v-alert__content {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .ffile-chip .v-chip__content {
    width: 100%;
  }

  .form-attachment [type="file"] {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    overflow: hidden;
    padding: 0;
    position: absolute !important;
    white-space: nowrap;
    width: 1px;
  }

  .form-attachment [type="file"] + label {
    background-color: #FFF;
    border: 1px solid #777;
    border-radius: 4px;
    color: #222;
    cursor: pointer;
    display: inline-block;
    padding: 2px 30px;
  }

  .form-attachment [type="file"]:focus + label,
  .form-attachment [type="file"] + label:hover {
    background-color: $primaryColor;
    color: #FFF;
  }

  .form-attachment [type="file"]:focus + label {
    outline: 1px dotted #000;
  }

</style>
